import type { BreadcrumbProps } from '@blueprintjs/core'
import { Card } from '../components/ui/card'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { Automations } from '../components/automations'
import { type Product, ProductsTable } from '../components/products/ProductsTable'
import type { ExpandedPerformanceMetrics } from '../components/table2/ad_grid_columns'
import { TableStore } from '../components/table2/table_store'
import { useSavedFiltersStore } from '../hooks/useSavedFiltersStore'
import { useUser } from '../hooks/useUser'
import { useTableHooksFromStore } from '../packages/blueprint-components/src/useTableHooks'
import { userStore } from '../stores/user_store'
import { adManagerPerformanceGet } from '../util/adManagerPerformanceApi'
import { isNavV2Enabled } from '../util/nav-version'
import { Overview } from './dashboard-overview-charts'

export const Dashboard = observer(() => {
    const { profile } = useUser()
    const { search } = useLocation()
    const useNavV2 = isNavV2Enabled(search)
    const [store] = useState<TableStore<Product & ExpandedPerformanceMetrics>>(
        new TableStore(userStore, 'products'),
    )
    const savedFiltersStore = useSavedFiltersStore()
    const tableHooks = useTableHooksFromStore(
        loadTable(profile),
        {},
        true,
        store,
    )

    const breadcrumbs: BreadcrumbProps[] = [{ text: 'Overview' }]

    return (
        <Layout
            breadcrumbs={breadcrumbs}
            primary="Dashboard"
            className={useNavV2 ? 'bg-white' : undefined}
            layoutClassName={useNavV2 ? '!p-0 md:!p-0 bg-white' : undefined}
        >
            {tableHooks?.items?.length > 0 && (
                <Overview tableHooks={tableHooks} useNavV2={useNavV2} />
            )}
            <Automations
                className={
                    useNavV2 ? '!rounded-none !shadow-none border-b border-[#E5E7EB]' : 'mt-4'
                }
                from={tableHooks?.params?.from}
                to={tableHooks?.params?.to}
            />
            <Card
                className={classNames(
                    'flex flex-col p-0',
                    useNavV2 ? '!rounded-none !shadow-none' : 'bp4-card-rounded mt-4',
                )}
            >
                <ProductsTable
                    tableHooks={tableHooks}
                    store={store}
                    savedFiltersStore={savedFiltersStore}
                    nested={true}
                />
            </Card>
        </Layout>
    )
})

const loadTable = (profile_id) => (params) => {
    if (!profile_id) {
        return Promise.resolve()
    }

    return adManagerPerformanceGet('api/v5/products', params, true)
}
